import {
  appGridApiAtom,
  appRowDataUpdatedAtAtom,
} from "@/components/AppGrid/AppGrid";
import { SidebarTitle } from "@/components/SidebarTitle/SidebarTitle";
import { TagPopoverEditor } from "@/components/TagPopoverEditor/TagPopoverEditor";
import { Tag, getPaletteById } from "@/models/tag";
import { Entity, EntityRecord } from "@/orm/types";

import {
  Badge,
  Group,
  ScrollArea,
  SegmentedControl,
  Stack,
} from "@mantine/core";
import { useAtomValue } from "jotai";
import { useEffect, useMemo, useState } from "react";
import { BiCog } from "react-icons/bi";

export const AppTagFilter = <T extends EntityRecord = EntityRecord>({
  entity,
  resourceProp,
  columnId,
  label = "Status",
}: {
  entity: Entity<T>;
  resourceProp: string;
  columnId: string;
  label?: string;
}) => {
  const api = useAtomValue(appGridApiAtom);
  const { data: item } = useAtomValue(entity.getObserveEntityRecordAtom());
  const [activeTagId, setActiveTagId] = useState("all");

  const resourceTags = item?.[
    resourceProp as keyof typeof item
  ] as unknown as Tag[];
  const tags = [{ id: "all", label: "All" }, ...(resourceTags || [])] as Tag[];
  const activeTag = tags.find((tag) => tag.id === activeTagId);
  const appRowDataUpdatedAt = useAtomValue(appRowDataUpdatedAtAtom);

  const onChange = (tagId: string) => {
    setActiveTagId(tagId);
  };

  useEffect(() => {
    api
      ?.setColumnFilterModel(
        columnId,
        activeTagId === "all"
          ? null
          : {
              filterType: "text",
              type: "contains",
              filter: activeTagId,
            },
      )
      ?.then(() => {
        api?.onFilterChanged();
      });
  }, [activeTagId]);

  const tagCounts = useMemo(() => {
    const countMap = new Map<string, number>();

    api?.forEachNode((node) => {
      const ids = node.data[columnId] as string[];
      ids?.forEach((id) => {
        countMap.set(id, (countMap.get(id) || 0) + 1);
        countMap.set("all", (countMap.get("all") || 0) + 1);
      });
    });

    return countMap;
  }, [appRowDataUpdatedAt]);

  if (!item) return null;

  return (
    <Stack>
      <SidebarTitle
        rightSection={
          <TagPopoverEditor entity={entity} resourceProp={resourceProp}>
            <BiCog size={18} />
          </TagPopoverEditor>
        }
      >
        {label}
      </SidebarTitle>
      <ScrollArea mah={"34dvh"} mih={200}>
        <SegmentedControl
          autoContrast
          color={
            getPaletteById(activeTag?.paletteId || "gray")?.backgroundColor
          }
          size="md"
          m={0}
          p={0}
          orientation="vertical"
          fullWidth
          withItemsBorders={false}
          onChange={onChange}
          data={tags.map((tag) => {
            return {
              ...tag,
              value: tag.id,
              label: (
                <Group justify="space-between">
                  <div>{tag.label}</div>
                  <Group gap={0}>
                    <Badge
                      fz={12}
                      size="md"
                      variant="filled"
                      miw={36}
                      color="gray.6"
                      style={{
                        color: getPaletteById(tag.paletteId)?.fontColor,
                        backgroundColor: getPaletteById(tag.paletteId)
                          ?.backgroundColor,
                      }}
                    >
                      {tagCounts.get(tag.id) || 0}
                    </Badge>
                  </Group>
                </Group>
              ),
            };
          })}
        />
      </ScrollArea>
    </Stack>
  );
};
